/**
 * DebugPanel — visual dependency-chain viewer for a session or single target.
 *
 * Shows a layered SVG graph where:
 *   - Each node  = execution unit (definition / for / content / import / use)
 *   - Group nodes (import / for / content) render as containers with child rows
 *   - Each edge  = $ref data-flow  upstream → downstream
 *   - Edges connect to the specific child row inside a group (port-based routing)
 *   - Target highlighted in sky-blue
 *
 * Target mode  (triggered with targetId):  3-column  deps → target → dependents
 * Session mode (no targetId):              topological layers of the full graph
 */

import type { SessionExplainGroupChild, SessionExplainResult } from "@magic-md/engine-client/protocol";
import type { LocaleParams } from "@magic-md/i18n";
import { useT } from "@magic-md/i18n/react";
import { AlertTriangle, Crosshair, Network, Target, XCircle } from "lucide-react";
import { type KeyboardEvent, type MouseEvent, useEffect, useRef, useState } from "react";
import { cn } from "../../lib/utils";
import { getPlatformService } from "../../platform";
import { useEditorStore } from "../../store/editor-store";
import { useLogStore } from "../../store/log-store";
import { usePluginExecutionStore } from "../../store/plugin-execution-store";
import { getThemeAccentStyle } from "../../theme/runtime/theme-accents";

/* ── layout constants ── */
const NODE_W = 130;
const NODE_H = 30;
const LAYER_GAP = 60;
const NODE_GAP = 8;
const GROUP_HEADER_H = 22;   // group header bar height
const GROUP_ITEM_H = 18;   // each child row height
const GROUP_ITEM_GAP = 3;    // gap between child rows
const GROUP_PAD_B = 6;    // bottom padding inside group
type EditorT = (key: string, params?: LocaleParams) => string;

/* ── status fill colors ── */
const STATUS_FILL: Record<string, string> = {
    success: "var(--status-success)",
    partial: "var(--status-warning)",
    error: "var(--status-error)",
    running: "var(--status-running)",
    idle: "var(--color-muted-foreground)",
};

const GRAPH_COLORS = {
    arrow: "color-mix(in srgb, var(--color-muted-foreground) 45%, transparent)",
    refArrow: "color-mix(in srgb, var(--color-warning) 70%, transparent)",
    refStroke: "color-mix(in srgb, var(--color-warning) 55%, transparent)",
    nodeFill: "color-mix(in srgb, var(--color-muted) 92%, transparent)",
    nodeStroke: "var(--color-border)",
    targetNodeFill:
        "color-mix(in srgb, var(--cm-explain-accent) 10%, transparent)",
    targetNodeStroke:
        "color-mix(in srgb, var(--cm-explain-accent) 55%, transparent)",
    label: "var(--color-foreground)",
    labelMuted: "var(--color-muted-foreground)",
} as const;

const TYPE_SHORT: Record<string, string> = {
    definition: "def",
    for: "for",
    content: "cnt",
    import: "imp",
    use: "use",
};

/** Color palette for each group type */
const GROUP_COLORS: Record<string, { header: string; bg: string; border: string; label: string; badge: string }> = {
    import: {
        header: "color-mix(in srgb, var(--cm-import-accent) 13%, transparent)",
        bg: "color-mix(in srgb, var(--cm-import-accent) 5%, transparent)",
        border: "color-mix(in srgb, var(--cm-import-accent) 45%, transparent)",
        label: "color-mix(in srgb, var(--cm-import-accent) 90%, var(--color-foreground))",
        badge: "color-mix(in srgb, var(--cm-import-accent) 65%, transparent)",
    },
    for: {
        header: "color-mix(in srgb, var(--cm-for-accent) 13%, transparent)",
        bg: "color-mix(in srgb, var(--cm-for-accent) 5%, transparent)",
        border: "color-mix(in srgb, var(--cm-for-accent) 45%, transparent)",
        label: "color-mix(in srgb, var(--cm-for-accent) 90%, var(--color-foreground))",
        badge: "color-mix(in srgb, var(--cm-for-accent) 65%, transparent)",
    },
    content: {
        header: "color-mix(in srgb, var(--cm-content-accent) 13%, transparent)",
        bg: "color-mix(in srgb, var(--cm-content-accent) 5%, transparent)",
        border: "color-mix(in srgb, var(--cm-content-accent) 45%, transparent)",
        label: "color-mix(in srgb, var(--cm-content-accent) 90%, var(--color-foreground))",
        badge: "color-mix(in srgb, var(--cm-content-accent) 65%, transparent)",
    },
};

const TARGET_COLORS = {
    header: "color-mix(in srgb, var(--cm-explain-accent) 12%, transparent)",
    bg: "color-mix(in srgb, var(--cm-explain-accent) 5%, transparent)",
    border: "color-mix(in srgb, var(--cm-explain-accent) 55%, transparent)",
    label: "color-mix(in srgb, var(--cm-explain-accent) 90%, var(--color-foreground))",
    badge: "color-mix(in srgb, var(--cm-explain-accent) 60%, transparent)",
};

/* ── layout types ── */
interface LayoutNode {
    id: string;
    x: number;
    y: number;
    status: string;
    unitType: string;
    isTarget: boolean;
    /** If set, this node renders as a group container with child rows */
    children?: SessionExplainGroupChild[];
}

interface LayoutEdge { from: string; to: string; kind?: string }

/** Visual height of a node (group nodes are taller) */
function nodeH(node: LayoutNode): number {
    const c = node.children;
    if (c && c.length > 0) {
        return GROUP_HEADER_H + c.length * (GROUP_ITEM_H + GROUP_ITEM_GAP) - GROUP_ITEM_GAP + GROUP_PAD_B;
    }
    return NODE_H;
}

/* ── session mode: assign horizontal layers via longest-path BFS ── */
function buildSessionLayout(
    nodes: SessionExplainResult["graph"]["nodes"],
    edges: SessionExplainResult["graph"]["edges"],
): { layoutNodes: LayoutNode[]; layoutEdges: LayoutEdge[] } {
    /* All nodes from engine are already top-level; children are embedded in node.children.
     * Strip "provides" edges (internal to group containers — already visualized as child rows). */
    const visibleEdges = edges.filter((e) => e.kind !== "provides");

    /* Build the set of all addressable IDs (top-level + all children).
     * Children are included so edge routing can target them. */
    const childOwner = new Map<string, string>(); // childId → parentId
    for (const n of nodes) {
        for (const c of n.children ?? []) childOwner.set(c.id, n.id);
    }

    /* For layer assignment, treat a child's edges as belonging to its parent */
    const resolveId = (id: string) => childOwner.get(id) ?? id;

    const inDeps = new Map<string, string[]>();
    for (const n of nodes) inDeps.set(n.id, []);
    for (const e of visibleEdges) {
        const from = resolveId(e.from);
        const to = resolveId(e.to);
        if (from !== to) inDeps.get(to)?.push(from);
    }

    const layerCache = new Map<string, number>();
    const getLayer = (id: string): number => {
        if (layerCache.has(id)) return layerCache.get(id)!;
        const ds = inDeps.get(id) ?? [];
        const layer = ds.length === 0 ? 0 : Math.max(...ds.map(getLayer)) + 1;
        layerCache.set(id, layer);
        return layer;
    };
    for (const n of nodes) getLayer(n.id);

    const byLayer = new Map<number, string[]>();
    for (const [id, layer] of layerCache) {
        if (!byLayer.has(layer)) byLayer.set(layer, []);
        byLayer.get(layer)!.push(id);
    }

    const nodeMap = new Map(nodes.map((n) => [n.id, n]));
    const layoutNodes: LayoutNode[] = [];

    for (const [layer, ids] of [...byLayer.entries()].sort(([a], [b]) => a - b)) {
        let cumulY = 0;
        for (const id of ids) {
            const n = nodeMap.get(id)!;
            const lNode: LayoutNode = {
                id,
                x: layer * (NODE_W + LAYER_GAP),
                y: cumulY,
                status: n.status,
                unitType: n.unitType,
                isTarget: n.isTarget ?? false,
                children: n.children,
            };
            layoutNodes.push(lNode);
            cumulY += nodeH(lNode) + NODE_GAP;
        }
    }

    return {
        layoutNodes,
        layoutEdges: visibleEdges.map((e) => ({ from: e.from, to: e.to, kind: e.kind })),
    };
}

/* ── target mode: 3-column  deps | target | dependents ── */
function buildTargetLayout(
    target: NonNullable<SessionExplainResult["target"]>,
    allNodes: SessionExplainResult["graph"]["nodes"],
): { layoutNodes: LayoutNode[]; layoutEdges: LayoutEdge[] } {
    const nodeMap = new Map(allNodes.map((n) => [n.id, n]));

    /* Build child → parent map: if a dep/dependent ID is a child, use its parent node */
    const childOwner = new Map<string, string>();
    for (const n of allNodes) {
        for (const c of n.children ?? []) childOwner.set(c.id, n.id);
    }
    const resolveId = (id: string) => childOwner.get(id) ?? id;

    /* Resolve deps / dependents to top-level node IDs, deduplicate */
    const depsResolved = [...new Set(target.dependencies.map(resolveId))];
    const dependentsResolved = [...new Set(target.dependents.map(resolveId))].filter(
        (id) => id !== target.id,
    );

    const makeLNode = (id: string, x: number, y: number): LayoutNode => {
        const n = nodeMap.get(id);
        return {
            id, x, y,
            status: n?.status ?? "idle",
            unitType: n?.unitType ?? "definition",
            isTarget: false,
            children: n?.children,
        };
    };

    /* Build deps column */
    const depsNodes: LayoutNode[] = [];
    let depsY = 0;
    for (const dep of depsResolved) {
        const ln = makeLNode(dep, 0, depsY);
        depsNodes.push(ln);
        depsY += nodeH(ln) + NODE_GAP;
    }

    /* Center target vertically */
    const targetNodeData = nodeMap.get(target.id);
    const targetNode: LayoutNode = {
        id: target.id,
        x: NODE_W + LAYER_GAP,
        y: Math.max(0, (depsY - NODE_GAP - NODE_H) / 2),
        status: target.status,
        unitType: target.unitType,
        isTarget: true,
        children: targetNodeData?.children,
    };

    /* Build dependents column */
    const dependentsNodes: LayoutNode[] = [];
    let depentsY = 0;
    for (const dep of dependentsResolved) {
        const ln = makeLNode(dep, 2 * (NODE_W + LAYER_GAP), depentsY);
        dependentsNodes.push(ln);
        depentsY += nodeH(ln) + NODE_GAP;
    }

    /* Edges from original dep IDs (may be child IDs) but routed to their resolved parent */
    const layoutEdges: LayoutEdge[] = [
        ...target.dependencies.map((depId) => ({
            from: depId,
            to: target.id,
            kind: "dependency" as const,
        })),
        ...target.dependents.map((depId) => ({
            from: target.id,
            to: depId,
            kind: "dependency" as const,
        })),
    ];

    return {
        layoutNodes: [...depsNodes, targetNode, ...dependentsNodes],
        layoutEdges,
    };
}

/* ── SVG dependency graph ── */
function DependencyGraph({
    layoutNodes,
    layoutEdges,
    onSelectUnit,
    t,
}: {
    layoutNodes: LayoutNode[];
    layoutEdges: LayoutEdge[];
    onSelectUnit: (unitId: string) => void;
    t: EditorT;
}) {
    if (layoutNodes.length === 0) return null;

    const activateUnit = (
        unitId: string,
        event?: MouseEvent<SVGGElement> | KeyboardEvent<SVGGElement>,
    ) => {
        event?.preventDefault();
        event?.stopPropagation();
        onSelectUnit(unitId);
    };

    const onUnitKeyDown = (
        unitId: string,
        event: KeyboardEvent<SVGGElement>,
    ) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        activateUnit(unitId, event);
    };

    /* posMap: top-level node positions + precomputed heights */
    const posMap = new Map(layoutNodes.map((n) => [n.id, { ...n, h: nodeH(n) }]));

    /* childPosMap: child-row anchor points for edge routing
     * Edges leaving a child exit from the RIGHT side; entering from the LEFT side. */
    interface ChildAnchor { x: number; y: number; parentX: number }
    const childPosMap = new Map<string, ChildAnchor>();
    for (const node of layoutNodes) {
        if (!node.children?.length) continue;
        for (let idx = 0; idx < node.children.length; idx++) {
            const child = node.children[idx];
            const rowY = node.y + GROUP_HEADER_H + idx * (GROUP_ITEM_H + GROUP_ITEM_GAP) + GROUP_ITEM_H / 2;
            childPosMap.set(child.id, { x: node.x + NODE_W, y: rowY, parentX: node.x });
        }
    }

    /** Resolve an edge endpoint to SVG coordinates */
    const getEndpoint = (id: string, side: "from" | "to"): { x: number; y: number } | null => {
        const cp = childPosMap.get(id);
        if (cp) {
            /* Edge leaves from right side, enters from left side of the child's row */
            return { x: side === "from" ? cp.x : cp.parentX, y: cp.y };
        }
        const np = posMap.get(id);
        if (np) {
            return { x: side === "from" ? np.x + NODE_W : np.x, y: np.y + np.h / 2 };
        }
        return null;
    };

    const svgW = Math.max(...layoutNodes.map((n) => n.x + NODE_W)) + 24;
    const svgH = Math.max(...layoutNodes.map((n) => n.y + nodeH(n))) + 24;

    /* Detect which group types are present for the legend */
    const presentTypes = new Set(layoutNodes.filter((n) => n.children?.length).map((n) => n.unitType));

    return (
        <div className="overflow-auto px-4 py-3">
            <svg
                width={svgW}
                height={svgH}
                className="overflow-visible block"
                role="img"
                aria-label={t("debug.graphAria")}
            >
                <defs>
                    <marker id="dbg-arrow" markerWidth="7" markerHeight="7" refX="6" refY="3" orient="auto">
                        <path
                            d="M0,0 L0,6 L7,3 z"
                            style={{ fill: GRAPH_COLORS.arrow }}
                        />
                    </marker>
                    <marker id="dbg-arrow-ref" markerWidth="7" markerHeight="7" refX="6" refY="3" orient="auto">
                        <path
                            d="M0,0 L0,6 L7,3 z"
                            style={{ fill: GRAPH_COLORS.refArrow }}
                        />
                    </marker>
                </defs>

                {/* Edges */}
                {layoutEdges.map((e, i) => {
                    const src = getEndpoint(e.from, "from");
                    const dst = getEndpoint(e.to, "to");
                    if (!src || !dst) return null;
                    const { x: x1, y: y1 } = src;
                    const { x: x2, y: y2 } = dst;
                    const dx = Math.abs(x2 - x1) * 0.45;
                    const isRef = e.kind === "ref";
                    return (
                        <path key={i}
                            d={`M ${x1} ${y1} C ${x1 + dx} ${y1} ${x2 - dx} ${y2} ${x2} ${y2}`}
                            fill="none"
                            style={{
                                stroke: isRef
                                    ? GRAPH_COLORS.refStroke
                                    : GRAPH_COLORS.nodeStroke,
                                strokeWidth: 1.5,
                                opacity: 0.85,
                                strokeDasharray: isRef ? "4 2" : undefined,
                            }}
                            markerEnd={isRef ? "url(#dbg-arrow-ref)" : "url(#dbg-arrow)"}
                        />
                    );
                })}

                {/* Nodes */}
                {layoutNodes.map((node) => {
                    const h = nodeH(node);
                    const isGroup = Boolean(node.children?.length) ||
                        node.unitType === "import" || node.unitType === "for" || node.unitType === "content";
                    const isTarget = node.isTarget;
                    const fill = STATUS_FILL[node.status] ?? STATUS_FILL.idle;
                    const safeId = node.id.replace(/[^a-zA-Z0-9]/g, "-");

                    if (isGroup) {
                        /* ── Group container (import / for / content) ── */
                        const grpColors = isTarget ? TARGET_COLORS : (GROUP_COLORS[node.unitType] ?? GROUP_COLORS.import);
                        const badge = TYPE_SHORT[node.unitType] ?? node.unitType.slice(0, 3);
                        const clipId = `dbg-clip-${safeId}`;
                        return (
                            <g
                                key={node.id}
                                transform={`translate(${node.x},${node.y})`}
                                role="button"
                                tabIndex={0}
                                aria-label={t("debug.viewUnitDetails", { id: node.id })}
                                onClick={(event) => activateUnit(node.id, event)}
                                onKeyDown={(event) => onUnitKeyDown(node.id, event)}
                                style={{ cursor: "pointer" }}
                            >
                                <title>{t("debug.viewUnitDetails", { id: node.id })}</title>
                                {/* Outer container */}
                                <rect width={NODE_W} height={h} rx={5}
                                    style={{ fill: grpColors.bg, stroke: grpColors.border, strokeWidth: isTarget ? 1.5 : 1 }} />
                                {/* Header bar */}
                                <rect width={NODE_W} height={GROUP_HEADER_H} rx={5}
                                    style={{ fill: grpColors.header, stroke: "none" }} />
                                <rect y={GROUP_HEADER_H - 5} width={NODE_W} height={5}
                                    style={{ fill: grpColors.header, stroke: "none" }} />
                                {/* Header status dot */}
                                <circle cx={12} cy={GROUP_HEADER_H / 2} r={3.5} style={{ fill }} />
                                <defs>
                                    <clipPath id={clipId}>
                                        <rect x={22} y={2} width={NODE_W - 44} height={GROUP_HEADER_H - 4} />
                                    </clipPath>
                                </defs>
                                {/* Group label */}
                                <text x={22} y={GROUP_HEADER_H / 2 + 4} fontSize={10}
                                    fontFamily="ui-monospace, 'SF Mono', Monaco, monospace"
                                    clipPath={`url(#${clipId})`}
                                    style={{ fill: grpColors.label }}>
                                    {node.id}
                                </text>
                                {/* Type badge */}
                                <text x={NODE_W - 6} y={GROUP_HEADER_H / 2 + 4} fontSize={8}
                                    fontFamily="ui-monospace, monospace" textAnchor="end"
                                    style={{ fill: grpColors.badge }}>
                                    {badge}
                                </text>
                                {/* Child rows */}
                                {(node.children ?? []).map((child, idx) => {
                                    const itemY = GROUP_HEADER_H + idx * (GROUP_ITEM_H + GROUP_ITEM_GAP);
                                    const rowClip = `dbg-clip-${safeId}-r${idx}`;
                                    return (
                                        <g
                                            key={child.id}
                                            transform={`translate(0,${itemY})`}
                                            role="button"
                                            tabIndex={0}
                                            aria-label={t("debug.viewUnitDetails", { id: child.id })}
                                            onClick={(event) => activateUnit(child.id, event)}
                                            onKeyDown={(event) => onUnitKeyDown(child.id, event)}
                                            style={{ cursor: "pointer" }}
                                        >
                                            <title>{t("debug.viewUnitDetails", { id: child.id })}</title>
                                            {idx > 0 && (
                                                <line x1={8} y1={0} x2={NODE_W - 8} y2={0}
                                                    style={{ stroke: grpColors.border, strokeWidth: 0.5, opacity: 0.4 }} />
                                            )}
                                            <circle cx={12} cy={GROUP_ITEM_H / 2} r={2.5}
                                                style={{ fill: STATUS_FILL[child.status] ?? STATUS_FILL.idle }} />
                                            <defs>
                                                <clipPath id={rowClip}>
                                                    <rect x={22} y={1} width={NODE_W - 30} height={GROUP_ITEM_H - 2} />
                                                </clipPath>
                                            </defs>
                                            <text x={22} y={GROUP_ITEM_H / 2 + 4} fontSize={9}
                                                fontFamily="ui-monospace, 'SF Mono', Monaco, monospace"
                                                clipPath={`url(#${rowClip})`}
                                                style={{
                                                    fill: GRAPH_COLORS.label,
                                                    opacity: 0.62,
                                                }}>
                                                {child.label}
                                            </text>
                                        </g>
                                    );
                                })}
                            </g>
                        );
                    }

                    /* ── Regular node ── */
                    const clipId = `dbg-clip-${safeId}`;
                    return (
                        <g
                            key={node.id}
                            transform={`translate(${node.x},${node.y})`}
                            role="button"
                            tabIndex={0}
                            aria-label={t("debug.viewUnitDetails", { id: node.id })}
                            onClick={(event) => activateUnit(node.id, event)}
                            onKeyDown={(event) => onUnitKeyDown(node.id, event)}
                            style={{ cursor: "pointer" }}
                        >
                            <title>{t("debug.viewUnitDetails", { id: node.id })}</title>
                            <rect width={NODE_W} height={NODE_H} rx={5} style={{
                                fill: isTarget
                                    ? GRAPH_COLORS.targetNodeFill
                                    : GRAPH_COLORS.nodeFill,
                                stroke: isTarget
                                    ? GRAPH_COLORS.targetNodeStroke
                                    : GRAPH_COLORS.nodeStroke,
                                strokeWidth: isTarget ? 1.5 : 1,
                            }} />
                            <circle cx={12} cy={NODE_H / 2} r={3.5} style={{ fill }} />
                            <defs>
                                <clipPath id={clipId}>
                                    <rect x={22} y={2} width={NODE_W - 36} height={NODE_H - 4} />
                                </clipPath>
                            </defs>
                            <text x={22} y={NODE_H / 2 + 4} fontSize={10}
                                fontFamily="ui-monospace, 'SF Mono', Monaco, monospace"
                                clipPath={`url(#${clipId})`}
                                style={{
                                    fill: GRAPH_COLORS.label,
                                    opacity: isTarget ? 0.9 : 0.72,
                                }}>
                                {node.id}
                            </text>
                            <text x={NODE_W - 6} y={NODE_H / 2 + 4} fontSize={8}
                                fontFamily="ui-monospace, monospace" textAnchor="end"
                                style={{
                                    fill: GRAPH_COLORS.labelMuted,
                                    opacity: 0.45,
                                }}>
                                {TYPE_SHORT[node.unitType] ?? node.unitType.slice(0, 3)}
                            </text>
                        </g>
                    );
                })}
            </svg>

            {/* Legend */}
            {presentTypes.size > 0 && (
                <div className="mt-3 flex flex-wrap items-center gap-3">
                    <span className="text-[9px] uppercase tracking-[0.1em] text-muted-foreground/30">{t("debug.legend")}</span>
                    {["import", "for", "content"].filter((type) => presentTypes.has(type)).map((type) => {
                        const c = GROUP_COLORS[type];
                        return (
                            <span key={type} className="inline-flex items-center gap-1">
                                <svg width="14" height="10">
                                    <rect width="14" height="10" rx="2"
                                        style={{ fill: c.bg, stroke: c.border, strokeWidth: 1 }} />
                                </svg>
                                <span style={{ color: c.label }} className="text-[9px]">
                                    {t(`debug.legend.${type}`)}
                                </span>
                            </span>
                        );
                    })}
                </div>
            )}
        </div>
    );
}


/* ── Compact execution order bar ── */
function ExecOrderBar({ ids, targetId, t }: { ids: string[]; targetId?: string; t: EditorT }) {
    if (ids.length === 0) return null;
    const MAX = 14;
    const shown = ids.slice(0, MAX);
    const hidden = ids.length - shown.length;
    const tIdx = targetId != null ? ids.indexOf(targetId) : -1;
    return (
        <div className="shrink-0 border-t border-border/30 px-4 py-2">
            <div className="mb-1 flex items-center justify-between">
                <span className="text-[9px] uppercase tracking-[0.12em] text-muted-foreground/40">{t("debug.executionOrder")}</span>
                {tIdx >= 0 && (
                    <span
                        className="text-[9px] theme-accent-text"
                        style={getThemeAccentStyle("explain")}
                    >
                        {t("debug.position", { index: tIdx + 1, total: ids.length })}
                    </span>
                )}
            </div>
            <div className="flex flex-wrap items-center gap-x-0.5 gap-y-0.5">
                {shown.map((id, i) => (
                    <span key={id} className="inline-flex items-center gap-px">
                        {i > 0 && <span className="select-none text-[9px] text-muted-foreground/20">→</span>}
                        <span
                            className={cn(
                                "rounded px-1 py-px font-mono text-[9px]",
                                id === targetId
                                    ? "theme-accent-subtle"
                                    : "text-muted-foreground/45",
                            )}
                            style={
                                id === targetId
                                    ? getThemeAccentStyle("explain")
                                    : undefined
                            }
                        >
                            {id}
                        </span>
                    </span>
                ))}
                {hidden > 0 && <span className="text-[9px] text-muted-foreground/30">+{hidden}</span>}
            </div>
        </div>
    );
}

/* ── Diagnostics bar ── */
function DiagnosticsBar({ diagnostics, t }: { diagnostics: SessionExplainResult["diagnostics"]; t: EditorT }) {
    if (diagnostics.length === 0) return null;
    return (
        <div className="shrink-0 border-t border-border/30 px-4 py-2">
            <div className="mb-1 text-[9px] uppercase tracking-[0.12em] text-muted-foreground/40">{t("debug.diagnostics", { count: diagnostics.length })}</div>
            <div className="flex flex-col gap-1">
                {diagnostics.map((d, i) => {
                    const isErr = d.level === "error";
                    return (
                        <div
                            key={`${d.code ?? d.message}-${i}`}
                            className={cn(
                                "flex items-start gap-1.5 rounded border px-2 py-1.5 text-[10px] theme-accent-banner",
                            )}
                            style={getThemeAccentStyle(isErr ? "error" : "warning")}
                        >
                            {isErr
                                ? <XCircle size={10} className="mt-px shrink-0" />
                                : <AlertTriangle size={10} className="mt-px shrink-0" />}
                            <span>{d.message}</span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

/* ── DebugPanel (exported) ── */
export function DebugPanel() {
    const t = useT("editor");
    const explainRequest = useLogStore((s) => s.explainRequest);
    const clearExplainRequest = useLogStore((s) => s.clearExplainRequest);
    const activeTabId = useEditorStore((s) => s.activeTabId);
    /** Live session for the currently active tab */
    const liveSessionId = usePluginExecutionStore((s) => s.sessionId);
    const tabSessionMap = usePluginExecutionStore((s) => s.tabSessionMap);
    const sessionId = liveSessionId ?? tabSessionMap.get(activeTabId) ?? null;

    const [result, setResult] = useState<SessionExplainResult | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    /**
     * showFull: when true, show the full session graph even if a target is set.
     * Resets to false whenever the explainRequest changes (new debug button click).
     */
    const [showFull, setShowFull] = useState(false);

    /* Clear the panel whenever the user switches to a different document tab */
    const prevTabRef = useRef(activeTabId);
    useEffect(() => {
        if (prevTabRef.current !== activeTabId) {
            prevTabRef.current = activeTabId;
            clearExplainRequest();
        }
    }, [activeTabId, clearExplainRequest]);

    /**
     * Derive the effective request:
     * - If an explicit explainRequest is set (user clicked a debug button), use it.
     * - Otherwise, if a session exists for the current doc, auto-load the full session graph.
     */
    const effectiveRequest = explainRequest ?? (sessionId ? { sessionId } : null);
    const isAutoSession = !explainRequest && effectiveRequest != null;

    useEffect(() => {
        setShowFull(false);   // reset toggle on new request
        if (!effectiveRequest) {
            setResult(null);
            setLoading(false);
            setError(null);
            return;
        }

        let cancelled = false;
        setLoading(true);
        setError(null);

        getPlatformService().engine
            .sessionExplain({
                sessionId: effectiveRequest.sessionId,
                targetId: effectiveRequest.targetId,
            })
            .then((next) => {
                if (cancelled) return;
                setResult(next);
                setLoading(false);
            })
            .catch((err) => {
                if (cancelled) return;
                setError(err instanceof Error ? err.message : String(err));
                setLoading(false);
            });

        return () => { cancelled = true; };
    }, [effectiveRequest?.sessionId, effectiveRequest?.targetId]);

    /* ── empty (no session yet) ── */
    if (!effectiveRequest) {
        return (
            <div className="flex h-full flex-col items-center justify-center gap-2 text-muted-foreground/30">
                <Crosshair size={20} strokeWidth={1.25} />
                <span className="text-[11px]">{t("debug.empty")}</span>
            </div>
        );
    }

    /* ── loading ── */
    if (loading) {
        return (
            <div className="flex h-full items-center justify-center gap-2 text-[11px] text-muted-foreground/40">
                <span className="h-2.5 w-2.5 animate-spin rounded-full border border-muted-foreground/20 border-t-muted-foreground/60" />
                {t("debug.analyzing")}
            </div>
        );
    }

    /* ── error ── */
    if (error) {
        return (
            <div
                className="m-3 flex items-start gap-2 rounded border px-3 py-2 text-[11px] theme-accent-banner"
                style={getThemeAccentStyle("error")}
            >
                <XCircle size={13} className="mt-px shrink-0" />
                {error}
            </div>
        );
    }

    if (!result) return null;

    const { graph, target, executionOrder, diagnostics } = result;

    /* Decide rendering mode.
     * Auto-session loads → always full graph (no target toggle bar needed).
     * Target-focused loads → show toggle if showFull is false */
    const useTargetMode = !isAutoSession && !showFull && target != null;

    /* build layout */
    let layoutNodes: LayoutNode[];
    let layoutEdges: LayoutEdge[];

    if (useTargetMode) {
        const built = buildTargetLayout(target!, graph.nodes);
        layoutNodes = built.layoutNodes;
        layoutEdges = built.layoutEdges;
    } else {
        const built = buildSessionLayout(graph.nodes, graph.edges);
        layoutNodes = built.layoutNodes;
        layoutEdges = built.layoutEdges;
    }

    const openUnitDebug = (unitId: string) => {
        useLogStore.getState().openDebugPanel({
            sessionId: effectiveRequest.sessionId,
            targetId: unitId,
        });
    };

    return (
        <div className="flex h-full flex-col overflow-hidden">
            {/* View-mode toggle bar — only shown when a target was explicitly requested */}
            {!isAutoSession && target && (
                <div className="flex shrink-0 items-center gap-1 border-b border-border/30 px-3 py-1">
                    <button
                        onClick={() => setShowFull(false)}
                        className={cn(
                            "inline-flex items-center gap-1 rounded px-2 py-0.5 text-[10px] transition-colors",
                            !showFull
                                ? "theme-accent-subtle"
                                : "text-muted-foreground/50 hover:text-muted-foreground",
                        )}
                        style={
                            !showFull
                                ? getThemeAccentStyle("explain")
                                : undefined
                        }
                    >
                        <Target size={10} />
                        {target.id}
                    </button>
                    <span className="text-[9px] text-muted-foreground/25">|</span>
                    <button
                        onClick={() => setShowFull(true)}
                        className={cn(
                            "inline-flex items-center gap-1 rounded px-2 py-0.5 text-[10px] transition-colors",
                            showFull
                                ? "theme-accent-subtle"
                                : "text-muted-foreground/50 hover:text-muted-foreground",
                        )}
                        style={
                            showFull
                                ? getThemeAccentStyle("explain")
                                : undefined
                        }
                    >
                        <Network size={10} />
                        {t("debug.globalDependencyGraph")}
                    </button>
                </div>
            )}

            {/* Graph — main scrollable area */}
            <div className="min-h-0 flex-1 overflow-auto">
                {layoutNodes.length > 0 ? (
                    <DependencyGraph
                        layoutNodes={layoutNodes}
                        layoutEdges={layoutEdges}
                        onSelectUnit={openUnitDebug}
                        t={t}
                    />
                ) : (
                    <div className="flex h-full items-center justify-center text-[11px] text-muted-foreground/30">
                        {t("debug.noUnits")}
                    </div>
                )}
            </div>



            {/* Execution order */}
            <ExecOrderBar
                ids={executionOrder}
                targetId={useTargetMode ? result.targetId : undefined}
                t={t}
            />

            {/* Diagnostics */}
            <DiagnosticsBar diagnostics={diagnostics} t={t} />
        </div>
    );
}
